import { useFormik } from 'formik';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { useTranslation } from "react-i18next";
import * as yup from 'yup';
import { withLazyMounting } from '../../../components/hoc/LazyDialog';
import { ScheduleWeek } from '../../../domain/ScheduleWeek';
import WeekSwitcher from '../../../components/WeekSwitcher';

interface Form {
    name: string;
}

interface Props {
    isOpen: boolean;
    weekDate: Date;
    onSubmit: (week: ScheduleWeek) => void;
    onClose: () => void;
}

const ScheduleWeekCreateDialog = (props: Props) => {
    const { t } = useTranslation();

    const validationSchema = yup.object().shape({
        name: yup.string().required(t("field_is_required", { name: t("name") })),
    });

    const formik = useFormik<Form>({
        validationSchema,
        initialValues: { name: '' },
        enableReinitialize: true,
        onSubmit: (values, { resetForm }) => {
            const week: ScheduleWeek = {
                name: values.name,
                date: props.weekDate,
                entries: [],
                skip: false,
            };

            props.onSubmit(week);
            resetForm();
            props.onClose();
        }
    });

    const { values, touched, errors, handleChange, handleBlur, handleSubmit, submitCount } = formik;
    const showNameError = Boolean(errors.name) && (touched.name || submitCount > 0);

    return (
        <Dialog open={props.isOpen} onClose={props.onClose}>
            <DialogTitle>{t("create_week")}</DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent>
                    <TextField
                        margin="normal"
                        id="name"
                        name="name"
                        label={t("name")}
                        fullWidth
                        variant="standard"
                        value={values.name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={showNameError}
                        helperText={showNameError ? errors.name : ""}
                    />
                    <div style={{ marginTop: 16 }}>
                        <WeekSwitcher
                            date={props.weekDate}
                            onDateUpdate={() => undefined}
                            fullWidth
                            disabled
                        />
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={props.onClose}>{t("cancel")}</Button>
                    <Button type="submit">{t("save")}</Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default withLazyMounting(ScheduleWeekCreateDialog);
